import React, { useEffect, useState, useMemo } from "react";
import "./OnlineBooking.css";
import {
  GetConfig,
  GetPaymentServiceDetails,
  UpdateTerms,
  GetSite,
  UpdateBookingPortalStatus
} from "./Requests";
import {
  PutAppointmentsConfig,
} from "../Settings/Requests";
import Select from "react-select";
import PrivacyPreviewCard from "./B2C";
import CustomButton from "../../Components/Reusable/Button/CustomButton";
import Payments from "./Payments";
import Theme from "./Theme";
import Details from "./Details";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { B2CGB } from "./B2CHTML-en-GB";
import { B2CES } from "./B2CHTML-es";
import { notifyError, notifySuccess, notifyWarn } from "../../Response/ResposeHandler";
import { useTranslation } from "react-i18next";

const OnlineBooking = (props) => {
  const { t } = useTranslation("onlineBooking");
  const { t: a } = useTranslation("settings");

  // Use stable keys for tabs and translate only labels for display
  const TAB_KEYS = {
    LINK: "links",
    THEME: "theme",
    DETAILS: "details",
    TANDCS: "tandcs",
    GDPR: "gdpr",
  };

    const bookingThresholds = [
    { value: 0, label: a("customerisation.bookings.options.book.noThreshold") },
    { value: 1, label: a("customerisation.bookings.options.book.h1") },
    { value: 2, label: a("customerisation.bookings.options.book.h2") },
    { value: 3, label: a("customerisation.bookings.options.book.h3") },
    { value: 4, label: a("customerisation.bookings.options.book.h4") },
    { value: 5, label: a("customerisation.bookings.options.book.dayBefore") },
  ];

  const cancellationThresholds = [
    { value: 0, label: a("customerisation.bookings.options.cancel.noThreshold") },
    { value: 1, label: a("customerisation.bookings.options.cancel.h1") },
    { value: 2, label: a("customerisation.bookings.options.cancel.h2") },
    { value: 3, label: a("customerisation.bookings.options.cancel.h8") },
    { value: 4, label: a("customerisation.bookings.options.cancel.h24") },
    { value: 5, label: a("customerisation.bookings.options.cancel.noCancel") },
  ];

  const [tab, setTab] = useState(TAB_KEYS.LINK);
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState();
  const [tAndCuttonLoading, setTAndCuttonLoading] = useState(false);
  const [status, setStatus] = useState("null");
  const [paymentServiceDetails, setPaymentServiceDetails] = useState();
  const [terms, setTerms] = useState();
  const [isBookingPortalAvailable, setIsBookingPortalAvailable] = useState();
  const [site, setSite] = useState("setup");
  const [bookingThreshold, setBookingThreshold] = useState();
  const [cancellationThreshold, setCancellationThreshold] = useState();
  const [saveIntervalButtonLoading, setSaveIntervalButtonLoading] = useState(false);

  useEffect(() => {
    GetConfigWrapper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function GetConfigWrapper() {
    GetConfig()
      .then((res) => {
        setConfig(res.data);
        setIsBookingPortalAvailable(res.data.isBookingPortalAvailable)
        setTerms(res.data.terms);
        if (!res.data.payoutsEnabled) setStatus("no payouts");
        if (!res.data.chargesEnabled) setStatus("no charges");
        if (res.data.payoutsEnabled && res.data.chargesEnabled) setStatus("Connected");
                setCancellationThreshold(
          cancellationThresholds.find((x) => x.value === res.data.cancellationThreshold)
        );
        setBookingThreshold(bookingThresholds.find((x) => x.value === res.data.bookingThreshold));
      })
      .catch(() => {
        // optionally toast
      })
      .finally(() => {
        GetPaymentServiceDetailsWrapper();
      });
  }

  function GetSiteWrapper() {
    GetSite()
      .then((res) => {
        setSite(res.data);
      })
      .catch(() => {
        // ignore
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function GetPaymentServiceDetailsWrapper() {
    GetPaymentServiceDetails()
      .then((res) => {
        if (res.status === 200) {
          setPaymentServiceDetails(res.data);
        }
      })
      .catch(() => {
        // ignore
      })
      .finally(() => {
        GetSiteWrapper();
      });
  }

  function SubmitTerms() {
    setTAndCuttonLoading(true);
    UpdateTerms(terms)
      .then((res) => {
        if (res.status === 200) {
          notifySuccess(t("onlineBooking.toasts.termsSaved"));
        }
      })
      .catch(() => {
        notifyError(t("onlineBooking.toasts.termsSaveError"));
      })
      .finally(() => {
        setTAndCuttonLoading(false);
      });
  }

    function LiveToggleHandler(e) {
      const next = e.target.checked;
      UpdateBookingPortalStatus(next)
        .then((res) => {
          if (res.status === 200 && next === true) {
            notifySuccess(t("onlineBooking.toasts.bookingPortalOnline"));
            setIsBookingPortalAvailable(true);
          }
          if (res.status === 200 && next === false) {
            notifySuccess(t("onlineBooking.toasts.bookingPortalOffline"));
            setIsBookingPortalAvailable(false);
          }
        })
        .catch((error) => {
            notifyError(t("onlineBooking.toasts.bookingPortalGenericError"));
            setIsBookingPortalAvailable((v) => v);
        })
        .finally(() => {});
    }

    function onCickAppointmentSettingsSave() {
      setSaveIntervalButtonLoading(true);
      PutAppointmentsConfig(cancellationThreshold.value, bookingThreshold.value)
        .then(() => {
          notifySuccess(a("toasts.saved"));
        })
        .catch(() => {
          notifyError(a("toasts.genericSaveError"));
        })
        .finally(() => {
          GetConfigWrapper();
          setSaveIntervalButtonLoading(false);
        });
    }

  const tabs = useMemo(
    () => [
      { key: TAB_KEYS.LINK, label: t("onlineBooking.tabs.link") },
      { key: TAB_KEYS.THEME,    label: t("onlineBooking.tabs.theme") },
      { key: TAB_KEYS.DETAILS,  label: t("onlineBooking.tabs.details") },
      { key: TAB_KEYS.TANDCS,   label: t("onlineBooking.tabs.tAndCs") },
      { key: TAB_KEYS.GDPR,     label: t("onlineBooking.tabs.gdpr") },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const renderTabs = () => {
    switch (tab) {
      case TAB_KEYS.LINK:
        return (
          <div>
            <ToastContainer />
              <div style={{marginBottom:"20px"}} className="mini-card-general gen-card">
                <div className="gen-toggleRow">
                  <div className="gen-toggleText">
                    <h3 className="gen-h3">{t("onlineBooking.bookingPortal.title")}</h3>
                    <p className="gen-muted">{t("onlineBooking.bookingPortal.description")}</p>
                  </div>

                  {/* Accessible switch */}
                  <div className="sectionToggle">
                    <input
                      className="toggle-switch"
                      type="checkbox"
                      checked={isBookingPortalAvailable}
                      id="live-toggle"
                      onChange={LiveToggleHandler}
                      // aria-label={t("labels.liveToggle")}
                    />
                    <label htmlFor="live-toggle" className="switch-label" />
                  </div>
                </div>
              </div>
          <Payments
            GetPaymentServiceDetails={GetPaymentServiceDetailsWrapper}
            setLoading={setIsLoading}
            getConfig={GetConfigWrapper}
            paymentServiceDetails={paymentServiceDetails}
            status={status}
            config={config}
            ShowBookingPortalLink={true}
            ShowPaymentPanel={false}
          />
                <div style={{marginTop:"20px"}} className="mini-card-general gen-card">
                  <div className="gen-toggleText">
                    <h3 className="gen-h3">{a("customerisation.bookings.titleBooking")}</h3>
                    <p className="gen-muted">{a("customerisation.bookings.descBooking")}</p>
                </div>
                <div className="schedBody">
                  <div style={{marginTop:"0.6rem"}} className="schedSelect">
                    <Select
                      isSearchable={false}
                      placeholder={a("customerisation.bookings.placeholders.booking")}
                      closeMenuOnSelect
                      options={bookingThresholds}
                      defaultValue={bookingThreshold}
                      onChange={(e) => setBookingThreshold({ value: e.value, label: e.label })}
                      classNamePrefix="rs"
                    />
                  </div>
              </div>
                  <div style={{marginTop:"2rem"}} className="gen-toggleText">
                    <h3 className="gen-h3">{a("customerisation.bookings.titleCancel")}</h3>
                    <p className="gen-muted">{a("customerisation.bookings.descCancel")}</p>
                </div>

                              <div className="schedBody">
                <div style={{marginTop:"0.6rem"}} className="schedSelect">
                  <Select
                    isSearchable={false}
                    placeholder={a("customerisation.bookings.placeholders.cancel")}
                    closeMenuOnSelect
                    options={cancellationThresholds}
                    value={cancellationThreshold}
                    onChange={(e) => setCancellationThreshold({ value: e.value, label: e.label })}
                    classNamePrefix="rs"
                  />
                </div>
                <p className="sm-hint" style={{ fontSize: "0.9rem" }}>
                  {a("customerisation.bookings.hintSupersede")}
                </p>
                <div className="infoActions mt-2">
                  <CustomButton
                    disabled={saveIntervalButtonLoading}
                    className="app-btn--sm"
                    onClick={onCickAppointmentSettingsSave}
                    label={a("common.save")}
                    loading={saveIntervalButtonLoading}
                  />
                </div>
              </div>
              </div>

          </div>
        );
      case TAB_KEYS.THEME:
        return (
          <div>
            <ToastContainer />
        <Theme config={config} site={site} getSite={GetSiteWrapper} />
        </div>
        );
      case TAB_KEYS.DETAILS:
        return (
        <div>
          <ToastContainer />
          <Details config={config} getConfig={GetConfigWrapper} />
        </div>
        );
      case TAB_KEYS.TANDCS:
        return (
          <div className="infoWrap">
            <ToastContainer />
            <section className="infoCard">
              <header className="infoHead" style={{ borderBottom: "none" }}>
                <h3>{t("onlineBooking.terms.title")}</h3>
                <p className="infoSub">{t("onlineBooking.terms.subtitle")}</p>
              </header>
              <div className="infoBody">
                <div className="sm-field">
                  <textarea
                    id="tac-textarea"
                    className="sm-textarea"
                    rows={7}
                    maxLength={500}
                    placeholder={t("onlineBooking.terms.placeholder")}
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                  />
                  <div className="sm-help">
                    {t("onlineBooking.terms.charCount", {
                      count: terms?.length ?? 0,
                      max: 500,
                    })}
                  </div>
                </div>
                <div className="infoActions">
                  <CustomButton
                    disabled={tAndCuttonLoading}
                    className="app-btn--sm"
                    onClick={SubmitTerms}
                    label={t("onlineBooking.actions.save")}
                    loading={tAndCuttonLoading}
                  />
                </div>
              </div>
            </section>
          </div>
        );
      case TAB_KEYS.GDPR:
        return (
          <div className="infoWrap">
            <ToastContainer />
            <PrivacyPreviewCard
              title={t("onlineBooking.privacy.title")}
              noticeHtml={config.bookingPortalLanguage === "en-GB" ? B2CGB : config.bookingPortalLanguage === "es" ? B2CES : B2CGB}
              businessName={config.shopName}
              businessEmail={config.emailForCustomer}
              supportEmail={process.env.REACT_APP_SUPPORT_EMAIL}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* <ToastContainer /> */}
      {isLoading ? (
        <div className="custom-loader-wrapper">
          <div className="custom-loader"></div>
        </div>
      ) : (
        <div className="container">
          <div className="sub-nav" style={{ marginTop: 0 }}>
            {tabs.map((tObj) => (
              <a
                key={tObj.key}
                href="#"
                className={`sub-tab ${tab === tObj.key ? "active" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  setTab(tObj.key);
                }}
              >
                {tObj.label}
              </a>
            ))}
          </div>
          {renderTabs()}
        </div>
      )}
    </div>
  );
};

export default OnlineBooking;
